import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { content } from "../../content/content";
import type { GalleryImage, GalleryTheme } from "../../data/gallery";
import { galleryImages, galleryThemes } from "../../data/gallery";
import { fadeUp, staggerContainer } from "../../hooks/useScrollReveal";

type Filter = "all" | GalleryTheme;

/** Deterministic tilt for the contact-sheet intro — no Math.random (SSR-safe). */
const INTRO_TILT = [-5, 3, -2, 4, -3];

const featured = galleryImages.filter((img) => img.featured);

export function Gallery() {
  const reduceMotion = useReducedMotion();
  const [filter, setFilter] = useState<Filter>("all");
  const [lightboxId, setLightboxId] = useState<string | null>(null);

  // The flat, ordered list of what's currently visible — drives lightbox paging.
  const visible = useMemo(
    () =>
      filter === "all"
        ? galleryImages
        : galleryImages.filter((img) => img.theme === filter),
    [filter],
  );

  const lightboxIndex = useMemo(
    () => visible.findIndex((img) => img.id === lightboxId),
    [visible, lightboxId],
  );

  const open = useCallback((id: string) => setLightboxId(id), []);
  const close = useCallback(() => setLightboxId(null), []);
  const page = useCallback(
    (dir: 1 | -1) => {
      setLightboxId((current) => {
        const i = visible.findIndex((img) => img.id === current);
        if (i === -1) {
          return current;
        }
        const next = (i + dir + visible.length) % visible.length;
        return visible[next]?.id ?? current;
      });
    },
    [visible],
  );

  const chips: { id: Filter; label: string }[] = [
    { id: "all", label: content.gallery.allLabel },
    // SAFETY: gallery theme ids are the Filter values by construction (content.json is validated by schema.ts).
    ...galleryThemes.map((t) => ({ id: t.id as Filter, label: t.label })),
  ];

  return (
    <section
      id="gallery"
      className="paper-grain min-h-screen bg-warm-cream/45 pt-28 pb-28 md:pt-36 md:pb-40"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header */}
        <div className="mb-10 max-w-2xl">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 font-body text-xs font-medium tracking-[0.18em] text-ink-muted uppercase transition-opacity hover:opacity-70"
          >
            <span aria-hidden="true">←</span>
            {content.gallery.backLabel}
          </Link>
          <p
            data-edit-id="content.gallery.eyebrow"
            className="mb-4 font-body text-xs font-medium tracking-[0.22em] text-saffron-dark uppercase"
          >
            {content.gallery.eyebrow}
          </p>
          <h1
            data-edit-id="content.gallery.headline"
            className="text-balance font-display text-[clamp(2rem,4.5vw,3.5rem)] text-ink"
          >
            {content.gallery.headline}
          </h1>
          <p
            data-edit-id="content.gallery.intro"
            className="mt-4 font-body text-lg text-ink-muted"
          >
            {content.gallery.intro}
          </p>
        </div>

        {/* Signature moment: a contact sheet that settles into place */}
        <div className="mb-14 flex flex-wrap gap-4 md:mb-20 md:gap-6">
          {featured.map((img, i) => (
            <motion.button
              key={img.id}
              type="button"
              onClick={() => open(img.id)}
              className="group relative block overflow-hidden rounded-sm bg-warm-white p-2 shadow-[0_10px_30px_-12px_rgba(26,26,46,0.4)] ring-1 ring-ink/5"
              initial={
                reduceMotion
                  ? false
                  : {
                      opacity: 0,
                      rotate: INTRO_TILT[i % INTRO_TILT.length],
                      y: 40,
                    }
              }
              animate={{ opacity: 1, rotate: 0, y: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.08 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={{ y: -4 }}
              aria-label={img.caption}
            >
              <img
                src={img.src}
                alt={img.caption}
                loading="eager"
                className={`block w-full rounded-[2px] object-cover ${
                  img.aspect === "portrait" ? "h-52 md:h-64" : "h-40 md:h-48"
                } ${img.aspect === "portrait" ? "aspect-[3/4]" : "aspect-[4/3]"}`}
              />
            </motion.button>
          ))}
        </div>

        {/* Filter chips */}
        <div className="mb-10 flex flex-wrap gap-3">
          {chips.map((chip) => {
            const isActive = chip.id === filter;
            return (
              <button
                key={chip.id}
                type="button"
                onClick={() => setFilter(chip.id)}
                aria-pressed={isActive}
                className={`rounded-2xl border px-5 py-2.5 font-body text-sm font-medium transition-colors duration-300 ${
                  isActive
                    ? "border-saffron-dark bg-saffron text-warm-white"
                    : "border-ink/12 bg-warm-white/70 text-ink-light hover:border-ink/30 hover:text-ink"
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>

        {/* Themed groups (All) or single filtered group */}
        {filter === "all" ? (
          galleryThemes.map((theme) => {
            const imgs = galleryImages.filter((img) => img.theme === theme.id);
            if (imgs.length === 0) {
              return null;
            }
            return (
              <ThemeGroup key={theme.id} heading={theme.heading}>
                <Mosaic images={imgs} onOpen={open} />
              </ThemeGroup>
            );
          })
        ) : (
          <Mosaic images={visible} onOpen={open} />
        )}
      </div>

      <AnimatePresence>
        {lightboxIndex !== -1 && visible[lightboxIndex] && (
          <Lightbox
            image={visible[lightboxIndex]}
            onClose={close}
            onPrev={() => page(-1)}
            onNext={() => page(1)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function ThemeGroup({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-16 md:mb-24">
      <div className="mb-6 flex items-center gap-4">
        <h2 className="font-display text-[clamp(1.3rem,2.2vw,1.9rem)] text-ink">
          {heading}
        </h2>
        <span className="h-px flex-1 bg-ink/12" aria-hidden="true" />
      </div>
      {children}
    </div>
  );
}

function Mosaic({
  images,
  onOpen,
}: {
  images: GalleryImage[];
  onOpen: (id: string) => void;
}) {
  return (
    <motion.div
      className="columns-1 gap-4 sm:columns-2 md:gap-5 lg:columns-3 lg:gap-6"
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
    >
      {images.map((img) => (
        <motion.figure
          key={img.id}
          variants={fadeUp}
          className="group relative mb-4 inline-block w-full overflow-hidden rounded-2xl md:mb-5 lg:mb-6"
          style={{ breakInside: "avoid" }}
        >
          <button
            type="button"
            onClick={() => onOpen(img.id)}
            className="block w-full cursor-zoom-in"
            aria-label={img.caption}
          >
            <img
              src={img.src}
              alt={img.caption}
              loading="lazy"
              className="block h-auto w-full transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <figcaption className="pointer-events-none absolute right-0 bottom-0 left-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent p-5 text-left font-body text-xs leading-snug text-warm-white/95 opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:text-sm">
              {img.caption}
            </figcaption>
          </button>
        </motion.figure>
      ))}
    </motion.div>
  );
}

function Lightbox({
  image,
  onClose,
  onPrev,
  onNext,
}: {
  image: GalleryImage;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        onPrev();
      } else if (e.key === "ArrowRight") {
        onNext();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm md:p-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={image.caption}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-warm-white/10 text-2xl text-warm-white transition-colors hover:bg-warm-white/20"
        aria-label="Close"
      >
        <span aria-hidden="true">×</span>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-warm-white/10 text-3xl text-warm-white transition-colors hover:bg-warm-white/20 md:left-6"
        aria-label="Previous"
      >
        <span aria-hidden="true">‹</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-warm-white/10 text-3xl text-warm-white transition-colors hover:bg-warm-white/20 md:right-6"
        aria-label="Next"
      >
        <span aria-hidden="true">›</span>
      </button>

      <motion.figure
        key={image.id}
        className="flex max-h-full max-w-5xl flex-col items-center"
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.caption}
          className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain shadow-2xl"
        />
        <figcaption className="mt-4 max-w-xl text-center font-body text-sm text-warm-white/80">
          {image.caption}
        </figcaption>
      </motion.figure>
    </motion.div>
  );
}
